/**
 * badgeCountRules — single source of truth for primary-rail badge
 * semantics per redesign §1.1.9 ("Badge semantics unified: every
 * top-level badge = 'items needing your action,' counted on the same
 * scale (item count, not entity count)").
 *
 * Why this module exists
 * ----------------------
 * The plan-completeness audit (GAP-BADGE-SEMANTICS-AUDIT — slice 92)
 * surfaced three concrete drift bugs that all share the same root
 * cause — there is no SSOT for what a badge on each rail item is
 * counting:
 *
 *   1. Reviews — counts open submissions (state = submitted | assessed)
 *      i.e. item count. Good citizen.
 *   2. At Risk — counts deduped MENTEES who have any of {idle ≥14d,
 *      overdue reviews ≥7d, open disputes}. Entity count, not item
 *      count. Numerically incomparable with Reviews.
 *   3. Needs Attention — sums open submissions + open disputes. Mixes
 *      semantics within the SAME badge.
 *
 * §1.1.9 fixes this by mandating the SAME unit ("items needing your
 * action") across every top-level rail badge. This module pins that
 * unit per rail key as a named, exhaustively-typed `Record` so future
 * authors cannot introduce drift by adding a rail item whose badge
 * counts entities instead of items.
 *
 * Discipline
 * ----------
 * - SOLID/SRP: this module owns the SEMANTIC contract (unit + tooltip
 *   copy). It deliberately does NOT own data fetching (orthogonal
 *   concern — see `MentorReviewDashboard.tsx` / `ConversationsView.tsx`
 *   for the per-rail data hooks). The audit-recommended hook
 *   (`usePrimaryRailBadges`) will compose this SSOT with those data
 *   hooks in a follow-up slice; this slice closes only the semantic
 *   gap.
 * - SOLID/OCP: adding a new rail item means (a) adding the key to
 *   `PrimaryRailKey` in `tabMetadata.ts` and (b) adding a row to
 *   `BADGE_COUNT_RULES` here. The exhaustive `Record<PrimaryRailKey,
 *   BadgeRule>` shape makes (b) a compile-time requirement — omitting
 *   the row is a TypeScript error, not a runtime miss.
 * - DRY: every consumer (PrimaryRail aria-label, future
 *   `usePrimaryRailBadges` hook, audit gap-closure tests) reads the
 *   unit + tooltip copy from here. No call site hardcodes
 *   "X unread" / "X items" — every string flows from this SSOT.
 * - KISS: a single `Record` literal + a `BadgeRule` value object + one
 *   `formatBadgeCount` helper for the 99+ overflow. No factory, no
 *   class hierarchy, no per-call configuration.
 * - Deterministic + idempotent: pure functions, no module-level
 *   mutable state, no `Date.now()`, no IO. Calling
 *   `tooltipTemplate(N)` with the same N always yields the same string.
 * - exactOptionalPropertyTypes: `threshold` is `?: number` (never
 *   `number | undefined`); helpers default to `OVERFLOW_THRESHOLD`
 *   when omitted rather than passing `undefined` explicitly.
 * - No new npm deps.
 *
 * Cross-references
 * ----------------
 * - Spec: `docs/redesign/mentorship-platform-redesign-2026-06-03.md`
 *   §1.1.9 (badge semantics unification), §22.21 (this slice).
 * - Audit: `docs/redesign/plan-completeness-audit-2026-06-03.md`
 *   (GAP-BADGE-SEMANTICS-AUDIT — Needs Attention / At Risk /
 *   Reviews numerical incomparability).
 * - Vocabulary SSOT: `src/core/navigation/tabMetadata.ts`
 *   (`PrimaryRailKey` is the exhaustive key set every row matches).
 * - Caller (presentation): `src/core/layout/PrimaryRail.tsx`
 *   (consumes `BADGE_COUNT_RULES[key].tooltipTemplate(badgeCount)`
 *   for the per-item aria-label / tooltip text — no magic strings
 *   at the call site).
 * - Sibling SSOTs (pattern reuse): `src/core/ui/metricCardThresholds.ts`
 *   (slice 88 — the named-rule constant + helper-fn pattern this
 *   module mirrors), `src/core/navigation/legacyRedirects.ts`
 *   (slice 91 — the exhaustive `Record` literal pattern).
 */

import type { PrimaryRailKey } from '../navigation/tabMetadata';
import { GLOSSARY } from '../../modules/mentorship/glossary';

// ── Glossary-sourced noun forms ───────────────────────────────────────────
//
// Why: per the no-naked-concept-strings rule, every user-facing concept
// word MUST source from `GLOSSARY.<KEY>.shortLabel` / `pluralLabel`
// rather than a literal string. The glossary stores Title-Case forms
// ("Submission", "Submissions") because rail-section titles + count
// chips render Title-Case; tooltip copy on the rail badges renders
// lower-case ("3 submissions need review"), so we lower-case the SSOT
// labels at this single boundary. A copy nudge in the glossary then
// flows through unchanged — and the lint rule is satisfied because no
// literal concept word ever appears at the call site.
const SUBMISSION_LABEL = GLOSSARY.SUBMISSION.shortLabel.toLowerCase();
const SUBMISSION_PLURAL = GLOSSARY.SUBMISSION.pluralLabel.toLowerCase();
// Phase 4.1: MEETING_* / INSIGHT_* labels retired alongside the
// schedule / insights rail rows. Re-introduce when those FEATURE_REGISTRY
// rows flip back to 'active' and earn a rail slot again.

// ── Types ─────────────────────────────────────────────────────────────────

/**
 * The canonical noun being counted on a rail badge. Per §1.1.9 every
 * top-level badge ultimately rolls up to "items needing your action" —
 * the per-key `unit` names what KIND of item that is so the tooltip
 * copy is honest about the underlying data shape.
 *
 * Why a union literal (not a free string):
 *   - Forces future rail-item authors to choose one of the audited
 *     units rather than invent ad-hoc nouns ("entries", "rows",
 *     "things"). Drift is a compile-time error.
 *   - The "semantic uniformity" test in `badgeCountRules.test.tsx`
 *     enforces that no two rules share an identical unit when their
 *     DOMAINS differ — two `'item'` rules are allowed (the union
 *     intent), but two rules with the same audit-specific unit
 *     (e.g. two `'mentee'` rules) would be ambiguous and fail.
 *
 * Members:
 *   - 'item'       — generic action item (catch-all for §1.1.9-aligned
 *                    aggregates: Home rollup, Settings nudges, etc.).
 *   - 'submission' — open submission (state = submitted | assessed).
 *                    The audit's Reviews source semantic — kept as a
 *                    distinct unit so the Mentorship rail tooltip can
 *                    say "submissions need review" honestly.
 *   - 'mentee'     — at-risk mentee (deduped entity count). NOTE:
 *                    §1.1.9 mandates item-count semantics for badges,
 *                    so 'mentee' is reserved for internal RailLayout
 *                    section badges only — it is NOT used for any
 *                    top-level rail key here. Listed in the union so
 *                    future internal callers can reuse the type without
 *                    a parallel definition.
 *   - 'thread'     — unread conversation thread. The audit's
 *                    ConversationsView source semantic — distinct from
 *                    'item' so the tooltip can say "unread threads".
 *   - 'meeting'    — upcoming meeting needing prep (Schedule rail).
 *   - 'insight'    — unread / unviewed insight (Insights rail).
 */
export type BadgeUnit =
    | 'item'
    | 'submission'
    | 'mentee'
    | 'thread'
    | 'meeting'
    | 'insight';

/**
 * Shape of a single per-rail badge rule. A rule is a value object
 * whose fields name the SOURCE of every literal that would otherwise
 * be inline at a call site:
 *
 *   - `unit`              names the noun.
 *   - `tooltipTemplate`   names the copy template (pure, deterministic).
 *   - `threshold`         names the overflow point (default 99 → "99+").
 *
 * Why: the shape is kept minimal — keeping it small makes the
 * exhaustive `Record<PrimaryRailKey, BadgeRule>` audit trivial to
 * read in code review. See docs/redesign/plan-completeness-audit-2026-06-03.md
 * (GAP-BADGE-SEMANTICS-AUDIT) for the field-set rationale and §1.1.9 for
 * the SSOT contract this shape codifies.
 */
export type BadgeRule = {
    /** Canonical noun this rule counts. See `BadgeUnit`. */
    unit: BadgeUnit;
    /**
     * Pure, deterministic tooltip / aria-label template. Receives the
     * raw integer count and returns the user-facing copy. The template
     * MUST expose the unit so the tooltip is honest about what is being
     * counted (the §1.1.9 contract). Implementations MUST handle the
     * zero case explicitly (the smart-count rule in `PrimaryRail.tsx`
     * suppresses the badge VISUAL when count === 0, but the aria-label
     * helper still calls `tooltipTemplate(0)` defensively to keep the
     * accessibility-tree announcements deterministic).
     */
    tooltipTemplate: (count: number) => string;
    /**
     * Display overflow threshold — when the raw count meets or exceeds
     * this value the visual badge renders as `${threshold}+` (e.g.
     * "99+") rather than the raw integer. Optional: omitted rules
     * default to `OVERFLOW_THRESHOLD` (99). Per spec §1.1.9 the
     * threshold is the SAME across rails so cross-rail counts stay
     * comparable; per-rule override exists only for future rail items
     * that may need a different ceiling (KISS — currently unused).
     */
    threshold?: number;
};

// ── Defaults ──────────────────────────────────────────────────────────────

/**
 * Module-level default overflow threshold. Per §1.1.9 + the audit
 * "numerically comparable" mandate, every top-level rail badge uses
 * the SAME ceiling so a Mentorship "99+" and a Conversations "99+"
 * mean the same upper-bound magnitude. The single source for the
 * literal lives here so a future spec nudge ("change 99 → 199") edits
 * ONE line.
 */
export const OVERFLOW_THRESHOLD = 99 as const;

/**
 * Pluralises the unit in the tooltip copy. Pure + locale-naive on
 * purpose — the rail tooltip is a 3-word string ("3 items need
 * attention") and ICU-level locale plural handling is overkill at
 * this layer. If the product ever ships i18n on rail copy, replace
 * this helper with the i18n shim; the SSOT shape doesn't change.
 *
 * Why no Intl.PluralRules: the audit's success bar is "stop the drift"
 * not "ship i18n in slice 92" — adding Intl here would inflate scope
 * and add a runtime dep on a polyfill in older browsers. Deferred to
 * the i18n rollout slice; see docs/redesign/plan-completeness-audit-2026-06-03.md
 * (GAP-BADGE-SEMANTICS-AUDIT) for the slice-92 scope cap.
 */
function pluralise(unit: BadgeUnit, count: number): string {
    return count === 1 ? unit : `${unit}s`;
}

/**
 * Generic "N items need attention" template factory. Used by rules
 * whose copy is the canonical §1.1.9 form so the literal "need
 * attention" appears in exactly one place. Per-rule overrides (e.g.
 * Conversations "unread threads") inline their own templates rather
 * than thread per-rule copy through this factory — keeping the SSOT
 * flat (KISS).
 */
function needsAttentionTemplate(unit: BadgeUnit): (count: number) => string {
    return (count: number) => {
        if (count === 0) return `No ${pluralise(unit, 0)} need attention`;
        return `${count} ${pluralise(unit, count)} need attention`;
    };
}

// ── BADGE_COUNT_RULES — the SSOT ─────────────────────────────────────────
//
// One row per `PrimaryRailKey`. The shape is exhaustive at compile time
// via `Record<PrimaryRailKey, BadgeRule>` — adding a new rail key in
// `tabMetadata.ts` without a matching row here is a TypeScript error,
// not a runtime miss. Order mirrors the canonical rail render order
// (home → mentorship → conversations → schedule → submissions →
// insights → settings) so a code-review diff reads top-to-bottom in
// the same order as the user-facing rail.
//
// Per-row rationale (audit-citation per row) appears inline so a
// future author tightening one row understands the audit constraint
// before changing the unit / copy.
export const BADGE_COUNT_RULES: Readonly<Record<PrimaryRailKey, BadgeRule>> = {
    /**
     * Home — the global "Inbox > Now" rollup. Aggregates everything
     * the user must triage on this surface (per §1.1 — Today + Needs
     * Attention de-duped into one ranked stream). Unit = 'item' so
     * the badge is numerically comparable with every other rail.
     */
    home: {
        unit: 'item',
        tooltipTemplate: needsAttentionTemplate('item'),
    },

    /**
     * Mentorship — open SUBMISSIONS awaiting first pass (state =
     * submitted | assessed). The audit's "Reviews" source semantic;
     * §1.1.9 keeps this as an item-count badge but names the unit
     * after the glossary SUBMISSION term so the tooltip ("3 submissions
     * need review") is honest about the underlying data shape AND
     * comparable to the other rails because one entry = one action item.
     *
     * Tooltip copy uses "need review" instead of the generic "need
     * attention" because the audit's UX pattern for the Mentorship
     * rail explicitly says "review" (the action verb on the surface
     * is "Review" not "Triage").
     */
    mentorship: {
        unit: SUBMISSION_LABEL as BadgeUnit,
        tooltipTemplate: (count: number) => {
            const word = count === 1 ? SUBMISSION_LABEL : SUBMISSION_PLURAL;
            if (count === 0) return `No ${SUBMISSION_PLURAL} need review`;
            return `${count} ${word} need review`;
        },
    },

    /**
     * Conversations — unread conversation THREADS (one count per
     * thread, regardless of how many unread messages within it).
     * The audit's ConversationsView source semantic. Unit = 'thread'
     * because per-thread count is the §1.1.9-aligned action item:
     * the mentor's action is "open the thread", not "read each
     * message".
     */
    conversations: {
        unit: 'thread',
        tooltipTemplate: (count: number) => {
            if (count === 0) return 'No unread threads';
            return `${count} unread ${pluralise('thread', count)}`;
        },
    },

    /**
     * Phase 4.1: schedule / submissions / insights rows removed from
     * BADGE_COUNT_RULES — these keys are no longer members of
     * `PrimaryRailKey` (FEATURE_REGISTRY classified them stub/hidden
     * under the workflow-first 6-key pivot). The schedule + insights
     * stub pages remain addressable by URL but render no rail badge;
     * promote the registry row to 'active' and re-add the rule here
     * when the full surface lands.
     */

    /**
     * Settings — outstanding setup / configuration nudges (profile
     * completion, 2FA prompt, billing-attention items). Unit =
     * 'item' (the catch-all per §1.1.9) because Settings nudges
     * are heterogeneous and the most honest unit is the generic
     * one — they all share the §1.1.9 "needs your action" contract.
     */
    settings: {
        unit: 'item',
        tooltipTemplate: needsAttentionTemplate('item'),
    },
};

// ── Helpers (pure, deterministic) ─────────────────────────────────────────

/**
 * Renders the visual badge text per the §1.1.9 overflow contract:
 * values at or above the threshold collapse to `${threshold}+`
 * (e.g. "99+"); everything else renders as the raw integer.
 *
 * Pure + deterministic — same `count` + same `rule` always returns
 * the same string. Caller threads the rule explicitly so this
 * module never reaches into the SSOT by key (KISS — the SSOT
 * lookup happens once, at the call site).
 *
 * @param count Raw integer count from the data hook.
 * @param rule  The rule (typically `BADGE_COUNT_RULES[key]`).
 */
export function formatBadgeCount(count: number, rule: BadgeRule): string {
    const threshold = rule.threshold ?? OVERFLOW_THRESHOLD;
    if (count >= threshold) return `${threshold}+`;
    return String(count);
}

/**
 * Resolves the per-key tooltip / aria-label string for a given
 * raw count. Thin wrapper over `BADGE_COUNT_RULES[key].tooltipTemplate`
 * so call sites do a single named call instead of indexing into the
 * Record AND invoking the template themselves.
 *
 * Why a wrapper exists: the audit's "no magic strings at the call
 * site" rule means PrimaryRail should NOT see the literal
 * `BADGE_COUNT_RULES[key].tooltipTemplate(...)` — it should call a
 * named helper. This is that helper.
 *
 * Pure + deterministic.
 *
 * @param key   The rail key being rendered.
 * @param count Raw integer count from the data hook (defaults to 0
 *              for accessibility-tree determinism when no badge is
 *              shown — see `PrimaryRail.tsx` aria-label composition).
 */
export function resolveBadgeTooltip(key: PrimaryRailKey, count: number): string {
    const rule = BADGE_COUNT_RULES[key];
    return rule.tooltipTemplate(count);
}
